import { useEffect, useRef, useState, type ReactNode, type Dispatch, type SetStateAction } from 'react';
import { TrackerAdvancedFilterBuilder, getSupportedTrackerOrderingColumns, TRACKER_GROUPING_OPTIONS, TRACKER_VIEW_MODE_OPTIONS, type SavedViewDefinition, type TrackerFilterField, type TrackerColumnDef, type TrackerViewMode } from '@nimbalyst/collab-bundle/trackers-ui';
import type { TrackerBrowseState } from './TrackerBrowseState';

export function TrackerPhoneSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current!;
    const previous = document.activeElement as HTMLElement | null;
    dialog.showModal();
    return () => { dialog.close(); previous?.focus(); };
  }, []);
  return (
    <dialog ref={ref} className="tracker-phone-sheet" aria-label={title} onCancel={event => { event.preventDefault(); onClose(); }} onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="tracker-phone-sheet-header"><strong>{title}</strong><button type="button" onClick={onClose} aria-label={`Close ${title}`}>Close</button></div>
      {children}
    </dialog>
  );
}

export function PhoneTrackerToolbar({ state, setState, fields, definition, columns, count, onCreate }: {
  state: TrackerBrowseState;
  setState: Dispatch<SetStateAction<TrackerBrowseState>>;
  fields: TrackerFilterField[];
  definition: SavedViewDefinition;
  columns: TrackerColumnDef[];
  count: number;
  onCreate?: () => void;
}) {
  const [sheet, setSheet] = useState<'filters' | 'more' | null>(null);
  const close = () => setSheet(null);
  const filters = state.columnFilters;
  const filterCount = (filters?.clauses.length ?? 0) + definition.activeFilters.length + definition.tagFilter.length + (definition.recentlyViewedDays ? 1 : 0);
  return (
    <div className="tracker-phone-toolbar">
      <input type="search" aria-label="Search tracker items" placeholder="Search plans and items" value={state.searchTerm} onChange={event => setState(current => ({ ...current, searchTerm: event.target.value }))} />
      <div className="tracker-phone-controls">
        <select aria-label="Item status scope" value={state.statusScope} onChange={event => setState(current => ({ ...current, statusScope: event.target.value as TrackerBrowseState['statusScope'] }))}>
          <option value="open">Open</option><option value="all">All</option><option value="closed">Closed</option>
        </select>
        <button type="button" onClick={() => setSheet('filters')}>Filters{filterCount ? ` (${filterCount})` : ''}</button>
        <span className="tracker-phone-count">{count}</span>
        <button type="button" onClick={() => setSheet('more')}>More</button>
      </div>
      {state.viewMode !== 'list' && state.phoneViewMode === null ? <div className="tracker-phone-presentation">List on phone · original view in More</div> : null}
      {sheet === 'filters' ? (
        <TrackerPhoneSheet title="Filters" onClose={close}>
          {(definition.activeFilters.length > 0 || definition.tagFilter.length > 0 || definition.recentlyViewedDays) ? <div className="tracker-saved-filter-summary">
            Saved view: {[...definition.activeFilters, ...definition.tagFilter.map(tag => `Tag: ${tag}`), ...(definition.recentlyViewedDays ? [`Viewed within ${definition.recentlyViewedDays} days`] : [])].join(' · ')}. These stay applied with your filters.
          </div> : null}
          <TrackerAdvancedFilterBuilder filterFields={fields} filters={filters} onFiltersChange={columnFilters => setState(current => ({ ...current, columnFilters }))} onBack={close} onClose={close} />
        </TrackerPhoneSheet>
      ) : null}
      {sheet === 'more' ? (
        <TrackerPhoneSheet title="View options" onClose={close}>
          <div className="tracker-phone-options">
            <label>Phone view<select value={state.phoneViewMode ?? 'auto'} onChange={event => setState(current => ({ ...current, phoneViewMode: event.target.value === 'auto' ? null : event.target.value as TrackerViewMode }))}>
              <option value="auto">Automatic list</option>
              {TRACKER_VIEW_MODE_OPTIONS.filter(option => option.value !== 'inbox').map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select></label>
            <label>Group by<select value={typeof state.groupBy === 'string' ? state.groupBy : `field:${state.groupBy.fieldId}`} onChange={event => setState(current => ({ ...current, groupBy: event.target.value.startsWith('field:') ? { kind: 'field', fieldId: event.target.value.slice(6) } : event.target.value as TrackerBrowseState['groupBy'] }))}>
              <option value="none">No grouping</option>
              {TRACKER_GROUPING_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              {state.groupBy !== 'none' && !TRACKER_GROUPING_OPTIONS.some(option => option.value === state.groupBy) ? <option value={typeof state.groupBy === 'string' ? state.groupBy : `field:${state.groupBy.fieldId}`}>{typeof state.groupBy === 'string' ? state.groupBy : state.groupBy.fieldId}</option> : null}
            </select></label>
            <label>Order by<select value={state.ordering} onChange={event => setState(current => ({ ...current, ordering: event.target.value }))}>
              <option value="manual">Manual</option>
              {getSupportedTrackerOrderingColumns(columns).map(column => <option key={column.id} value={column.id}>{column.label}</option>)}
              {!columns.some(column => column.id === state.ordering) && state.ordering !== 'manual' ? <option value={state.ordering}>{state.ordering}</option> : null}
            </select></label>
            {onCreate ? <button type="button" onClick={() => { close(); onCreate(); }}>New item</button> : null}
          </div>
        </TrackerPhoneSheet>
      ) : null}
    </div>
  );
}
